import React, { useState } from 'react';

interface Topic {
  topic_id: number;
  topic_name: string;
  total_questions_attempted: number;
  correct_answers: number;
  incorrect_answers: number;
  skipped_questions: number;
  accuracy_percentage: number;
  total_time_spent_seconds: number;
  average_time_per_question_seconds: number;
}

interface Subject {
  subject_id: number;
  subject_name: string;
  topics: Topic[];
  subject_summary: {
    total_topics: number;
    total_questions_attempted: number;
    total_correct_answers: number;
    subject_accuracy_percentage: number;
    total_time_spent_seconds: number;
  };
}

interface TopicPerformanceData {
  user_id: number;
  overall_stats: {
    total_subjects: number;
    total_topics: number;
    total_questions_attempted: number;
    total_correct_answers: number;
    overall_accuracy_percentage: number;
    total_time_spent_seconds: number;
  };
  subjects: Subject[];
}

interface TopicPerformanceChartProps {
  data: TopicPerformanceData;
}

const TopicPerformanceChart: React.FC<TopicPerformanceChartProps> = ({ data }) => {
  const [expandedSubjects, setExpandedSubjects] = useState<Set<number>>(new Set());
  const [sortBy, setSortBy] = useState<'accuracy' | 'attempted' | 'time'>('accuracy');
  const [filterAccuracy, setFilterAccuracy] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  const toggleSubject = (subjectId: number) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'warning';
    if (accuracy >= 40) return 'info';
    return 'danger';
  };

  const getAccuracyBadgeClass = (accuracy: number) => {
    if (accuracy >= 80) return 'bg-success';
    if (accuracy >= 60) return 'bg-warning';
    if (accuracy >= 40) return 'bg-info';
    return 'bg-danger';
  };

  const sortTopics = (topics: Topic[]) => {
    return [...topics].sort((a, b) => {
      switch (sortBy) {
        case 'accuracy':
          return b.accuracy_percentage - a.accuracy_percentage;
        case 'attempted':
          return b.total_questions_attempted - a.total_questions_attempted;
        case 'time':
          return b.total_time_spent_seconds - a.total_time_spent_seconds;
        default:
          return 0;
      }
    });
  };

  const filterTopics = (topics: Topic[]) => {
    if (filterAccuracy === 'all') return topics;
    
    return topics.filter(topic => {
      switch (filterAccuracy) {
        case 'high':
          return topic.accuracy_percentage >= 70;
        case 'medium':
          return topic.accuracy_percentage >= 40 && topic.accuracy_percentage < 70;
        case 'low':
          return topic.accuracy_percentage < 40;
        default:
          return true;
      }
    });
  };

  const processedSubjects = data.subjects.map(subject => ({
    ...subject,
    topics: filterTopics(sortTopics(subject.topics))
  }));

  return (
    <div className="card border-0 shadow-sm" style={{ borderRadius: '15px' }}>
      <div className="card-header bg-transparent border-0 pt-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-3 gap-3">
          <h5 className="card-title fw-bold mb-0">
            <i className="fas fa-list-check me-2 text-primary"></i>
            Subject-wise Topic Performance
          </h5>
          <div className="d-flex flex-column flex-sm-row gap-2 w-80 w-lg-auto">
            <select 
              className="form-select form-select-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              style={{ minWidth: '160px' }}
            >
              <option value="accuracy">Sort by Accuracy</option>
              <option value="attempted">Sort by Questions</option>
              <option value="time">Sort by Time</option>
            </select>
            <select 
              className="form-select form-select-sm"
              value={filterAccuracy}
              onChange={(e) => setFilterAccuracy(e.target.value as any)}
              style={{ minWidth: '180px' }}
            >
              <option value="all">All Topics</option>
              <option value="high">High Performance (≥70%)</option>
              <option value="medium">Medium Performance (40-69%)</option>
              <option value="low">Low Performance (&lt;40%)</option>
            </select>
          </div>
        </div>
        
        {/* Overall Stats */}
        <div className="row g-3 mb-3">
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Total Subjects</small>
              <strong className="text-primary">{data.overall_stats.total_subjects}</strong>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Total Topics</small>
              <strong className="text-info">{data.overall_stats.total_topics}</strong>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Questions</small>
              <strong className="text-warning">{data.overall_stats.total_questions_attempted}</strong>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Correct</small>
              <strong className="text-success">{data.overall_stats.total_correct_answers}</strong>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Accuracy</small>
              <strong className={`text-${getAccuracyColor(data.overall_stats.overall_accuracy_percentage)}`}>
                {data.overall_stats.overall_accuracy_percentage.toFixed(1)}%
              </strong>
            </div>
          </div>
          <div className="col-md-2">
            <div className="text-center p-2 bg-light rounded">
              <small className="text-muted d-block">Study Time</small>
              <strong className="text-secondary">{formatTime(data.overall_stats.total_time_spent_seconds)}</strong>
            </div>
          </div>
        </div>
      </div>
      
      <div className="card-body">
        {processedSubjects.map((subject, subjectIndex) => (
          <div key={subject.subject_id} className="mb-4">
            {/* Subject Header */}
            <div 
              className="d-flex justify-content-between align-items-center p-3 rounded cursor-pointer"
              style={{ backgroundColor: '#f8f9fa', cursor: 'pointer' }}
              onClick={() => toggleSubject(subject.subject_id)}
            >
              <div className="d-flex align-items-center">
                <i className={`fas fa-chevron-${expandedSubjects.has(subject.subject_id) ? 'down' : 'right'} me-3 text-muted`}></i>
                <div>
                  <h6 className="mb-1 fw-bold text-dark">{subject.subject_name}</h6>
                  <small className="text-muted">
                    {subject.subject_summary.total_topics} topics • {subject.subject_summary.total_questions_attempted} questions
                  </small>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="text-center">
                  <small className="text-muted d-block">Accuracy</small>
                  <span className={`badge ${getAccuracyBadgeClass(subject.subject_summary.subject_accuracy_percentage)}`}>
                    {subject.subject_summary.subject_accuracy_percentage.toFixed(1)}%
                  </span>
                </div>
                <div className="text-center">
                  <small className="text-muted d-block">Time</small>
                  <strong className="text-secondary">{formatTime(subject.subject_summary.total_time_spent_seconds)}</strong>
                </div>
              </div>
            </div>

            {/* Subject Topics (Expandable) */}
            {expandedSubjects.has(subject.subject_id) && (
              <div className="mt-3">
                {subject.topics.length === 0 ? (
                  <div className="text-center p-4 text-muted">
                    <i className="fas fa-filter me-2"></i>
                    No topics match the current filter criteria.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th scope="col" className="fw-semibold">Topic Name</th>
                          <th scope="col" className="text-center fw-semibold">Accuracy</th>
                          <th scope="col" className="text-center fw-semibold">Total</th>
                          <th scope="col" className="text-center fw-semibold">Correct</th>
                          <th scope="col" className="text-center fw-semibold">Incorrect</th>
                          <th scope="col" className="text-center fw-semibold">Skipped</th>
                          <th scope="col" className="text-center fw-semibold">Time Spent</th>
                          <th scope="col" className="text-center fw-semibold">Avg Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subject.topics.map((topic, topicIndex) => (
                          <tr key={topic.topic_id} className="border-bottom">
                            <td>
                              <div className="fw-semibold text-dark" style={{ fontSize: '0.9rem', lineHeight: '1.3' }}>
                                {topic.topic_name}
                              </div>
                            </td>
                            <td className="text-center">
                              <span className={`badge ${getAccuracyBadgeClass(topic.accuracy_percentage)} px-2 py-1`}>
                                {topic.accuracy_percentage.toFixed(1)}%
                              </span>
                            </td>
                            <td className="text-center">
                              <strong className="text-primary">{topic.total_questions_attempted}</strong>
                            </td>
                            <td className="text-center">
                              <strong className="text-success">{topic.correct_answers}</strong>
                            </td>
                            <td className="text-center">
                              <strong className="text-danger">{topic.incorrect_answers}</strong>
                            </td>
                            <td className="text-center">
                              <strong className="text-warning">{topic.skipped_questions}</strong>
                            </td>
                            <td className="text-center">
                              <span className="text-secondary">
                                {topic.total_time_spent_seconds > 0 ? formatTime(topic.total_time_spent_seconds) : '-'}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="text-muted">
                                {topic.average_time_per_question_seconds > 0 ? 
                                  `${topic.average_time_per_question_seconds.toFixed(1)}s` : '-'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        
        {processedSubjects.length === 0 && (
          <div className="text-center py-5">
            <i className="fas fa-chart-bar text-muted mb-3" style={{ fontSize: '3rem' }}></i>
            <h5 className="text-muted">No Topic Performance Data</h5>
            <p className="text-muted">Complete some quizzes to see your topic-wise performance analysis.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicPerformanceChart;
